// 範囲指定（マーキー）選択のための純粋な幾何ヘルパー。
// ドラッグの始点・終点（ステージ座標）から正規化した矩形を作り、その矩形に
// 重なるオブジェクト ID を求める。DOM やストアには依存しないため単体テストしやすい。

import { isAabbContained, type CanvasObjectType } from "@refinement-canvas/domain";
import { aabbIntersects, type Aabb } from "./culling";
import { penStrokeIntersectsRect, type PenHitPoint } from "./pen-stroke-hit";

/** 範囲指定枠（ステージ座標・幅高さは常に非負）。 */
export interface MarqueeRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * ドラッグ始点 (x0,y0) と現在点 (x1,y1) から、左上原点・非負サイズの矩形へ正規化する。
 * どの向きにドラッグしても同じ矩形として扱えるようにする。
 */
export function normalizeMarqueeRect(
  x0: number,
  y0: number,
  x1: number,
  y1: number
): MarqueeRect {
  return {
    x: Math.min(x0, x1),
    y: Math.min(y0, y1),
    w: Math.abs(x1 - x0),
    h: Math.abs(y1 - y0),
  };
}

/** objectIdsInMarquee の種別ごとの当たり判定オプション。 */
export interface MarqueeHitOptions {
  /** ペンストロークの当たり判定に加えるマージン（stage 座標）。 */
  readonly penMarginStagePx?: number | undefined;
}

/**
 * 範囲指定枠に AABB が重なる（辺が接する場合も含む）オブジェクトの ID を返す。
 * 回転は考慮せず、各オブジェクトの x/y/w/h（未回転 AABB）で交差判定する。
 * 描画順とは無関係に Object.entries の列挙順で返す。
 *
 * 例外（セクション）: セクションは大型の背景コンテナで、重なり判定だと「セクション内の
 * 付箋を囲うだけのつもりが背後のセクションごと選択され、移動で内容物まで巻き込まれる」
 * 事故が起きるため、**マーキー矩形に完全に包含される場合のみ**選択対象にする。
 *
 * 例外（ペンストローク）: ストロークの外接矩形は線が通っていない空白を大きく含むため、
 * 外接矩形の交差だと「囲む空白だけを囲ったのに選択される」ことになる。そこで
 * **折れ線を太さ + マージンだけ広げた帯**と交差する場合のみ選択対象にする。
 * ただし帯（近似）と描画（perfect-freehand の実アウトライン）は別系統の幾何であり
 * 厳密な包含を保証できないため、**枠が外接矩形を完全に包含する場合は幾何判定によらず
 * 選択する**（「全体を囲んだのに選択されない」という最悪ケースを原理的に排除する）。
 * 回転済みのペンストロークは折れ線と描画位置が一致しないため、従来の外接矩形判定へ
 * フォールバックする。
 */
export function objectIdsInMarquee<
  T extends Aabb & {
    readonly type?: CanvasObjectType | undefined;
    readonly points?: readonly PenHitPoint[] | undefined;
    readonly thickness?: number | undefined;
    readonly rotation?: number | undefined;
  },
>(
  objects: Record<string, T>,
  rect: MarqueeRect,
  options?: MarqueeHitOptions
): string[] {
  const requestedMargin = options?.penMarginStagePx ?? 0;
  // 負値・NaN・Infinity は 0 として扱い、粗フィルタの矩形を壊さないようにする。
  const penMargin =
    Number.isFinite(requestedMargin) && requestedMargin > 0 ? requestedMargin : 0;
  const ids: string[] = [];
  for (const [id, object] of Object.entries(objects)) {
    const box = { x: object.x, y: object.y, w: object.w, h: object.h };
    if (object.type === "section") {
      if (isAabbContained(box, rect)) ids.push(id);
      continue;
    }
    const points = object.points;
    if (
      object.type === "pen-stroke" &&
      points &&
      points.length > 0 &&
      (object.rotation ?? 0) === 0
    ) {
      // 粗フィルタ: マージンで広げた外接矩形と交差しないストロークは、点数に依らず定数時間で除外する。
      // 保存される外接矩形は computePenStrokeFrame が折れ線へ thickness だけ余白を足したものなので、
      // 帯（折れ線 ± (thickness / 2 + margin)）は必ずこの拡張矩形の内側に収まる＝取りこぼさない。
      const padded = {
        x: box.x - penMargin,
        y: box.y - penMargin,
        w: box.w + penMargin * 2,
        h: box.h + penMargin * 2,
      };
      if (!aabbIntersects(rect, padded)) continue;
      if (
        isAabbContained(box, rect) ||
        penStrokeIntersectsRect(points, object.thickness ?? 0, penMargin, rect)
      ) {
        ids.push(id);
      }
      continue;
    }
    if (aabbIntersects(rect, box)) {
      ids.push(id);
    }
  }
  return ids;
}
